import { getDsdCompareHistory } from "@/apis/dsd-dsd-compare/route";
import type {
  DsdCompareHistoryItem,
  DsdCompareHistoryResponse,
} from "@/apis/dsd-dsd-compare/types";
import queries from "@/queries";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

const DEFAULT_LIMIT = 20;

/**
 * 비교 내역 페이지네이션. 내역은 대사(compare) 호출이 만들므로 다른 탭에서 대사한 결과는
 * 무효화 시점이 없다 — 최신 목록을 바로 보여야 하는 화면은 `staleTime: 0` 으로 받는다.
 */
export const useDsdCompareHistory = (
  limit: number = DEFAULT_LIMIT,
  { staleTime = 1000 * 60 * 5 }: { staleTime?: number } = {},
) => {
  const query = useInfiniteQuery({
    queryKey: queries.dsdDsdCompare()._ctx.history(limit).queryKey,
    queryFn: ({ pageParam }) =>
      getDsdCompareHistory({ cursor: pageParam, limit }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: DsdCompareHistoryResponse) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime,
    gcTime: 1000 * 60 * 10,
  });

  const items = useMemo<DsdCompareHistoryItem[]>(() => {
    if (!query.data) return [];
    return query.data.pages.flatMap((page) => page.items);
  }, [query.data]);

  return {
    ...query,
    items,
  };
};
